<template>
  <div class="dashboard">
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h1 class="h3 mb-1">Dashboard</h1>
        <p class="text-muted mb-0">Welcome back, {{ currentAdmin?.email }}</p>
      </div>
      <div class="text-muted">
        <i class="fas fa-calendar-alt me-2"></i>
        {{ currentDate }}
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="row mb-4">
      <div class="col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <i class="fas fa-map-marked-alt fa-2x mb-3"></i>
            <h3>{{ tripsCount }}</h3>
            <p>Total Trips</p>
          </div>
        </div>
      </div>

      <div class="col-md-6 mb-3">
        <div class="card stats-card">
          <div class="card-body text-center">
            <i class="fas fa-star fa-2x mb-3"></i>
            <h3>{{ reviewsCount }}</h3>
            <p>Total Reviews</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <i class="fas fa-bolt me-2"></i>
              Quick Actions
            </h5>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6 col-lg-3 mb-3">
                <router-link
                  to="/admin/trips/create"
                  class="btn btn-primary w-100"
                >
                  <i class="fas fa-plus me-2"></i>
                  Add New Trip
                </router-link>
              </div>
              <div class="col-md-6 col-lg-3 mb-3">
                <router-link
                  to="/admin/reviews/create"
                  class="btn btn-secondary w-100"
                >
                  <i class="fas fa-star me-2"></i>
                  Add Review
                </router-link>
              </div>
              <div class="col-md-6 col-lg-3 mb-3">
                <router-link
                  to="/admin/trips"
                  class="btn btn-outline-primary w-100"
                >
                  <i class="fas fa-list me-2"></i>
                  Manage Trips
                </router-link>
              </div>
              <div class="col-md-6 col-lg-3 mb-3">
                <router-link
                  to="/admin/reviews"
                  class="btn btn-outline-secondary w-100"
                >
                  <i class="fas fa-comments me-2"></i>
                  Manage Reviews
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistics Configuration -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              <i class="fas fa-chart-bar me-2"></i>
              Configure Statistics
            </h5>
            <button @click="toggleAdminPanel" class="btn btn-sm btn-outline-primary">
              <i class="fas fa-cog me-1"></i>
              {{ showAdminPanel ? 'Hide' : 'Configure' }}
            </button>
          </div>
          <div v-if="showAdminPanel" class="card-body">
            <div class="row">
              <div class="col-md-4 mb-3">
                <label class="form-label">Amazing Trips:</label>
                <input
                  v-model.number="adminStats.amazingTrips"
                  type="number"
                  min="0"
                  class="form-control"
                />
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Happy Travelers:</label>
                <input
                  v-model.number="adminStats.happyTravelers"
                  type="number"
                  min="0"
                  class="form-control"
                />
              </div>
              <div class="col-md-4 mb-3">
                <label class="form-label">Destinations:</label>
                <input
                  v-model.number="adminStats.destinations"
                  type="number"
                  min="0"
                  class="form-control"
                />
              </div>
            </div>
            <div class="d-flex justify-content-end">
              <button @click="updateStatistics" class="btn btn-primary">
                <i class="fas fa-save me-1"></i>
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Recent Activity -->
    <div class="row">
      <!-- Recent Trips -->
      <div class="col-lg-6 mb-4">
        <div class="card">
          <div
            class="card-header d-flex justify-content-between align-items-center"
          >
            <h5 class="mb-0">
              <i class="fas fa-clock me-2"></i>
              Recent Trips
            </h5>
            <router-link
              to="/admin/trips"
              class="btn btn-sm btn-outline-primary"
            >
              View All
            </router-link>
          </div>
          <div class="card-body">
            <div v-if="tripsLoading" class="text-center py-3">
              <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <div
              v-else-if="recentTrips.length === 0"
              class="text-center py-3 text-muted"
            >
              <i class="fas fa-info-circle me-2"></i>
              No trips found
            </div>
            <div v-else>
              <div
                v-for="trip in recentTrips"
                :key="trip.id"
                class="d-flex align-items-center mb-3 pb-3 border-bottom"
              >
                <div
                  v-if="trip.images && trip.images.length > 0"
                  class="flex-shrink-0 me-3"
                >
                  <img
                    :src="trip.images[0]"
                    :alt="trip.place"
                    class="rounded"
                    style="width: 50px; height: 50px; object-fit: cover"
                  />
                </div>
                <div class="flex-grow-1">
                  <h6 class="mb-1">{{ trip.place }}</h6>
                  <small class="text-muted">
                    {{ trip.days }} days - &#8377;{{ trip.price }}
                  </small>
                  <br />
                  <small class="text-muted">
                    {{ formatDate(trip.createdAt) }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recent Reviews -->
      <div class="col-lg-6 mb-4">
        <div class="card">
          <div
            class="card-header d-flex justify-content-between align-items-center"
          >
            <h5 class="mb-0">
              <i class="fas fa-comments me-2"></i>
              Recent Reviews
            </h5>
            <router-link
              to="/admin/reviews"
              class="btn btn-sm btn-outline-secondary"
            >
              View All
            </router-link>
          </div>
          <div class="card-body">
            <div v-if="reviewsLoading" class="text-center py-3">
              <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <div
              v-else-if="recentReviews.length === 0"
              class="text-center py-3 text-muted"
            >
              <i class="fas fa-info-circle me-2"></i>
              No reviews found
            </div>
            <div v-else>
              <div
                v-for="review in recentReviews"
                :key="review.id"
                class="mb-3 pb-3 border-bottom"
              >
                <div
                  class="d-flex justify-content-between align-items-start mb-2"
                >
                  <h6 class="mb-0">{{ review.reviewerName }}</h6>
                  <div class="text-warning">
                    <i
                      v-for="n in 5"
                      :key="n"
                      :class="
                        n <= review.rating ? 'fas fa-star' : 'far fa-star'
                      "
                    ></i>
                  </div>
                </div>
                <p class="text-truncate-2 mb-1">{{ review.reviewText }}</p>
                <small class="text-muted">
                  {{ formatDate(review.createdAt) }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "Dashboard",
  data() {
    return {
      currentDate: moment().format("MMMM DD, YYYY"),
      showAdminPanel: false,
      adminStats: {
        amazingTrips: 7,
        happyTravelers: 8,
        destinations: 50,
      },
      statistics: {
        amazingTrips: 7,
        happyTravelers: 8,
        destinations: 50,
      },
    };
  },
  computed: {
    ...mapGetters("auth", ["currentAdmin"]),
    ...mapGetters("trips", {
      allTrips: "allTrips",
      tripsLoading: "isLoading",
    }),
    ...mapGetters("reviews", {
      allReviews: "allReviews",
      reviewsLoading: "isLoading",
    }),

    tripsCount() {
      return this.allTrips.length;
    },

    reviewsCount() {
      return this.allReviews.length;
    },

    recentTrips() {
      return [...this.allTrips]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5);
    },

    recentReviews() {
      return [...this.allReviews]
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5);
    },
  },
  methods: {
    ...mapActions("trips", ["fetchTrips"]),
    ...mapActions("reviews", ["fetchReviews"]),

    formatDate(dateString) {
      return moment(dateString).format("MMM DD, YYYY");
    },

    async loadDashboardData() {
      try {
        await Promise.all([this.fetchTrips(), this.fetchReviews(), this.loadStatistics()]);
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      }
    },

    // Load statistics from backend
    async loadStatistics() {
      try {
        const response = await axios.get('/statistics');
        if (response.data.success) {
          this.statistics = response.data.statistics;
        }
      } catch (error) {
        console.error('Error loading statistics:', error);
        // Keep default values if API fails
      }
    },

    // Save statistics to backend (admin only)
    async saveStatistics(newStats) {
      try {
        const response = await axios.put('/statistics', newStats, {
          headers: {
            'Authorization': `Bearer ${this.$store.getters['auth/authToken']}`
          }
        });
        if (response.data.success) {
          this.statistics = { ...newStats };
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error saving statistics:', error);
        return false;
      }
    },

    // Admin panel methods
    toggleAdminPanel() {
      this.showAdminPanel = !this.showAdminPanel;
      if (this.showAdminPanel) {
        this.adminStats = { ...this.statistics };
      }
    },

    async updateStatistics() {
      const success = await this.saveStatistics(this.adminStats);
      this.showAdminPanel = false;

      if (success) {
        Swal.fire({
          title: "Success!",
          text: "Statistics updated successfully!",
          icon: "success",
          timer: 2000,
        });
      } else {
        Swal.fire({
          title: "Error!",
          text: "Failed to update statistics. Please try again.",
          icon: "error",
        });
      }
    },
  },
  async created() {
    await this.loadDashboardData();
  },
};
</script>

<style scoped>
.dashboard {
  animation: fadeIn 0.5s ease-out;
}

.stats-card {
  background: linear-gradient(
    135deg,
    var(--primary-color),
    var(--secondary-color)
  );
  color: white;
  border: none;
  transition: transform 0.3s ease;
}

.stats-card:hover {
  transform: translateY(-5px);
}

.stats-card .card-body {
  padding: 1.5rem;
}

.stats-card h3 {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.stats-card p {
  margin: 0;
  opacity: 0.9;
  font-weight: 500;
}

.border-bottom:last-child {
  border-bottom: none !important;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  /* Ensure emojis render correctly */
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", "Android Emoji", "EmojiSymbols", "EmojiOne Mozilla", "Twemoji Mozilla", "Segoe UI", "Helvetica Neue", Arial, sans-serif;
  font-feature-settings: "liga" off;
  -webkit-font-feature-settings: "liga" off;
  -moz-font-feature-settings: "liga" off;
  text-rendering: optimizeLegibility;
}

@media (max-width: 768px) {
  .dashboard {
    padding: 0;
  }

  .stats-card .card-body {
    padding: 1rem;
  }

  .stats-card h3 {
    font-size: 1.5rem;
  }
}
</style>
